import React from 'react';
import parse, { domToReact } from 'html-react-parser';
import Grid from '@mui/material/Grid2';
import { Button, Typography } from '@mui/material';

const htmlConvert = ({ htmlContent }) => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return null;
  }

  // Function to handle mapping grid classes
  const replaceColClasses = (node) => {
    if (node.name === 'div' && node.attribs?.class?.includes('col-')) {
      const classList = node.attribs.class.split(' ');
      const gridSizes = {};
      const gridOrder = {};
      const customClasses = [];

      classList.forEach((cls) => {
        if (cls.startsWith('col-')) {
          const [_, size, value] = cls.split('-');
          if (size === 'md') gridSizes.md = parseInt(value, 10);
          if (size === 'sm') gridSizes.sm = parseInt(value, 10);
          if (size === 'xs') gridSizes.xs = parseInt(value, 10);
        } else if (cls.startsWith('order-')) {
          const [_, size, value] = cls.split('-');
          if (size === 'md') gridOrder.md = parseInt(value, 10);
          if (size === 'sm') gridOrder.sm = parseInt(value, 10);
          if (size === 'xs') gridOrder.xs = parseInt(value, 10);
        } else {
          customClasses.push(cls); // Collect custom classes
        }
      });

      return (
        <Grid
          size={gridSizes}
          order={gridOrder}
          className={customClasses.join(' ')}
          sx={node.attribs?.style ? parseStyle(node.attribs.style) : {}} // Preserve inline styles
        >
          {domToReact(node.children, { replace: replaceNodes })}
        </Grid>
      );
    }

    // Replace buttons
    if (node.name === 'a' && node.attribs?.class?.includes('btn')) {
      return (
        <Button
          component="a"
          variant="contained"
          color="primary"
          href={node.attribs.href}
          className={node.attribs.class}
          sx={node.attribs?.style ? parseStyle(node.attribs.style) : {}} // Preserve inline styles
        >
          {domToReact(node.children, { replace: replaceNodes })}
        </Button>
      );
    }

    // Replace headings
    if (node.name === 'h2') {
      return (
        <Typography
          variant="h4"
          component="h2"
          className={node.attribs?.class || ''}
          sx={node.attribs?.style ? parseStyle(node.attribs.style) : {}} // Preserve inline styles
        >
          {domToReact(node.children, { replace: replaceNodes })}
        </Typography>
      );
    }

    // Replace paragraphs
    if (node.name === 'p') {
      const customClasses = node.attribs?.class || '';
      return (
        <Typography
          variant="body1"
          className={customClasses}
          sx={node.attribs?.style ? parseStyle(node.attribs.style) : {}} // Preserve inline styles
        >
          {domToReact(node.children, { replace: replaceNodes })}
        </Typography>
      );
    }
  };

  // Helper function to parse inline style strings into an object
  const parseStyle = (styleString) => {
    const styleObj = {};
    styleString.split(';').forEach((style) => {
      const [key, value] = style.split(':').map((s) => s.trim());
      if (key && value) {
        // Convert CSS property to camelCase for React (e.g., text-align -> textAlign)
        const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        styleObj[camelKey] = value;
      }
    });
    return styleObj;
  };

  // Unified replace function
  const replaceNodes = (node) => {
    if (node.name === 'div' && node.attribs?.class) {
      const classList = node.attribs.class.split(' ');
      const alignmentClass = classList.find((cls) =>
        cls.startsWith('align-items-')
      );
      const customClasses = classList.join(' ');

      if (node.attribs.class.includes('row')) {
        return (
          <Grid
            container
            spacing={3}
            alignItems={alignmentClass ? alignmentClass.replace('align-items-', '') : 'center'}
            className={customClasses}
            sx={node.attribs?.style ? parseStyle(node.attribs.style) : {}} // Preserve inline styles
          >
            {domToReact(node.children, { replace: replaceNodes })}
          </Grid>
        );
      }

      if (
        node.attribs.class.includes('container-fluid') ||
        node.attribs.class.includes('container')
      ) {
        return (
          <Grid
            container
            spacing={3}
            className={customClasses}
            sx={node.attribs?.style ? parseStyle(node.attribs.style) : {}} // Preserve inline styles
          >
            {domToReact(node.children, { replace: replaceNodes })}
          </Grid>
        );
      }
    }

    return replaceColClasses(node); // Handle other cases
  };

  return parse(htmlContent, { replace: replaceNodes });
};

export default htmlConvert;